import { Bot as GrtsBot } from 'grts-utils/types/Bot';
import { Exchange } from '../../types/exchange';
import { Transaction } from '../Overview/types';
import {
  CbrtRangeState,
  CurrentProfit,
  MrcState as CalculatedMrcState,
  Transaction as ProfitTransaction,
  IndicatorConfig,
  RSIItem,
} from '../ProfitCalculations/types';
// import { TimeFrame } from '../../types/Bot';

export type BotTimeFrames = {
  higher: TimeFrame;
  takeProfit?: TimeFrame;
  shorter: TimeFrame;
  multiOrder: TimeFrame;
};

export type BotComplexParams = {
  cbrt: number | null;
  maxDecimals: number;
  maxMultiOrderAmount: number;
  maxOrderSlippage: number;
  shorterTimeFrameInterval: number;
  startBalance: number;
  secondHighestProfitTimeFrame: string;
  thirdHighestProfitTimeFrame: string;
};

export type Bot = {
  timeFrame: BotTimeFrames;
  baseAsset: string;
  botId: string;
  isActive: boolean;
  isEnabled: boolean;
  quoteAsset: string;
  runtime?: {
    cbrtRange?: any;
    didEarlierOrder?: {
      value: boolean;
      timestamp: number;
    };
    latestOrder?: string;
    latestTransactionType?: string;
    multiOrder?: { position: number };
    retryUpdateComplexParams?: boolean | null;
  };
  complexParams?: BotComplexParams;
  currentProfit: number;
  dateCreated?: string;
  defaultExchange: Exchange;
  activePort?: number;
  lastUpdated: number;
  availableExchanges: Exchange[];
};

export type Candle = {
  id?: number;
  startDate: string;
  startTimestamp: number;
  endDate: string;
  endTimestamp: number;
  open: number;
  close: number;
  low: number;
  high: number;
  direction: string;
  startOfNewTrend: boolean | null;
  decision: boolean | null;
  didMakeDecision: boolean | null;
};

export enum CandleType {
  Normal = 'NORMAL',
  Heikin = 'HEIKIN',
}

export type DataPoint = {
  x: number;
  open: number;
  close: number;
  low: number;
  high: number;
};
export enum TimeFrame {
  TwoMonths = '2M',
  OneMonth = '1M',
  ThreeWeeks = '3w',
  TwoWeeks = '2w',
  OneAndHalfWeek = '1.5w',
  TenDays = '10d',
  OneWeek = '1w',
  SixDays = '6d',
  FiveDays = '5d',
  HundredHours = '100h',
  FourDays = '4d',
  HalfWeek = '0.5w',
  ThreeDays = '3d',
  TwoDays = '2d',
  OneDay = '1d',
  TwentyHours = '20h',
  EighteenHours = '18h',
  FifteenHours = '15h',
  TwelveHours = '12h',
  TenHours = '10h',
  EightHours = '8h',
  SixHours = '6h',
  FiveHours = '5h',
  FourHours = '4h',
  ThreeHours = '3h',
  TwoHours = '2h',
  HundredMinutes = '100m',
  SeventyFiveMinutes = '75m',
  OneHour = '1h',
  FiftyMinutes = '50m',
  FortyMinutes = '40m',
  ThirtyMinutes = '30m',
  TwentyMinutes = '20m',
  FifteenMinutes = '15m',
  TenMinutes = '10m',
  EightMinutes = '8m',
  SixMinutes = '6m',
  FiveMinutes = '5m',
  FourMinutes = '4m',
  ThreeMinutes = '3m',
  TwoMinutes = '2m',
  OneMinute = '1m',
  // New for TPTs
  HundredSixtyHours = '160h',
  HundredFiftySixHours = '156h',
  HundredFiftyTwoHours = '152h',
  HundredFiftyHours = '150h',
  HundredFortySixHours = '146h',
  HundredFortyFourHours = '144h',
  HundredThirtySixHours = '136h',
  HundredThirtyFourHours = '134h',
  HundredThirtyTwoHours = '132h',
  HundredThirtyHours = '130h',
  HundredTwentyEightHours = '128h',
  HundredTwentySixHours = '126h',
  HundredFourteenHours = '114h',
  HundredTwelveHours = '112h',
  HundredTenHours = '110h',
  HundredEightHours = '108h',
  HundredSixHours = '106h',
  NinetyTwoHours = '92h',
  NinetyHours = '90h',
  EightyEightHours = '88h',
  EightySixHours = '86h',
  EightyFourHours = '84h',
  EightyHours = '80h',
  SeventyEightHours = '78h',
  SeventySixHours = '76h',
  SeventyFiveHours = '75h',
  SeventyThreeHours = '73h',
  SeventyTwoHours = '72h',
  SixtyEightHours = '68h',
  SixtySixHours = '66h',
  SixtyFiveHours = '65h',
  SixtyThreeHours = '63h',
  SixtyHours = '60h', // 2.5 days
  FiftyEightHours = '58h',
  FiftySevenHours = '57h',
  FiftySixHours = '56h',
  FiftyFiveHours = '55h',
  FiftyFourHours = '54h',
  FiftyThreeHours = '53h',
  FiftyTwoHours = '52h',
  FiftyHours = '50h',
  FortySixHours = '46h',
  FortyFiveHours = '45h',
  FortyFourHours = '44h',
  FortyThreeHours = '43h',
  FortyTwoHours = '42h',
  FortyHours = '40h',
  ThirtyEightHours = '38h',
  ThirtySixHours = '36h',
  ThirtyHours = '30h',
  TwentyNineHours = '29h',
  TwentyEightHours = '28h',
  TwentySevenHours = '27h',
  TwentySixHours = '26h',
  TwentyThreeHours = '23h',
  TwentyTwoHours = '22h',
  TwentyOneHours = '21h',
  NineteenHours = '19h',
  FourteenHours = '14h',
  ElevenHours = '11h',
  NineHours = '9h',
  SevenHours = '7h',
}

export enum Scale {
  Linear = 'linear',
  Logarithmic = 'log',
}

export enum Tabs {
  Charts = 'Charts',
  Transactions = 'Transactions',
  Control = 'Control',
  ExtraInfo = 'Extra Info',
}

export interface TransactionDetailsApiResponse {
  allTransactions: Transaction[];
  compactTransactions: Transaction[];
  currentProfit: CurrentProfit | undefined;
}

export interface ProfitApiResponse {
  total: {
    stable: number;
    asset: number;
  };
  current: {
    stable: number;
    asset: number;
  };
  noAlgorithmicProgressionProfit: number;
}

export type SubscriptionBalance = {
  stable: {
    start: number | null;
    reserve: number;
    current: number;
    shortCode: string | null;
  };
  asset: {
    start: number | null;
    reserve: number;
    current: number;
    shortCode: string | null;
  };
};

export type Subscription = {
  id: number;
  botId: string;
  clusterId: string;
  userId: number;
  email: string | null | undefined;
  isActive: boolean;
  isAutomatedOrder: boolean;
  isNotificationEnabled: boolean;
  fee: {
    latest: number;
    total: number;
  };
  totalValue: number;
  multiOrder?: {
    volume: number;
    initialVolume: number;
  };
};

export type CbrtRange = {
  isActive: boolean;
  open: number;
  direction: string;
  timestamp: number;
};

export type ComplexParamsBundle = {
  complexParams: Partial<BotComplexParams>;
  timeFrames: BotTimeFrames;
  indicatorConfig?: IndicatorConfig;
};

export type LatestBotState = {
  orderType: string;
  currentProfit: number;
  currentPriceChange: number;
  candles: Candle[];
  cbrtRangeStates: CbrtRangeState[];
  mrcStates: CalculatedMrcState[];
  transactions: ProfitTransaction[];
  bot?: GrtsBot;
  baseAsset: string;
  quoteAsset: string;
  timeFrame: TimeFrame;
  exchange: Exchange;
  startTimestamp?: number;
  endTimestamp?: number;
  indicatorConfig?: IndicatorConfig;
  rsiIndicatorValuesOppositeOrder: RSIItem[] | undefined;
  rsiIndicatorValuesSameOrder: RSIItem[] | undefined;
};
